import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Loader2, LogIn } from 'lucide-react';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        document.documentElement.classList.add('dark');
        if (user) navigate('/');
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await login(email, password);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-[#09090B]" data-testid="login-page">
            {/* Left: Image */}
            <div className="hidden lg:block relative">
                <img
                    src="https://images.unsplash.com/photo-1768974522003-6ce1ccc096b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZ3JhaW4lMjBzaWxvJTIwbmlnaHQlMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzcxNjgwNDA0fDA&ixlib=rb-4.1.0&q=85"
                    className="absolute inset-0 w-full h-full object-cover opacity-50"
                    alt="Industrial grain silo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-transparent" />
                <div className="absolute bottom-16 left-12 right-12">
                    <h2 className="text-4xl sm:text-5xl font-bold text-white font-heading tracking-tight">
                        AgriTrade<br />FinOps
                    </h2>
                    <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-md">
                        Agricultural commodity trading operations, inventory management, and financial reporting in one unified platform.
                    </p>
                </div>
            </div>

            {/* Right: Login Form */}
            <div className="flex items-center justify-center p-6 sm:p-8">
                <Card className="w-full max-w-md bg-zinc-900/50 border-white/5 backdrop-blur-xl" data-testid="login-card">
                    <CardHeader className="space-y-1 pb-4">
                        <div className="lg:hidden mb-4">
                            <h1 className="text-2xl font-bold font-heading text-white">AgriTrade</h1>
                            <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">FinOps Platform</p>
                        </div>
                        <CardTitle className="text-xl font-heading text-zinc-100">Sign In</CardTitle>
                        <CardDescription className="text-zinc-500">Enter your credentials to access the platform</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm" data-testid="login-error">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label className="text-zinc-300">Email</Label>
                                <Input
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    type="email"
                                    placeholder="admin@ge.com"
                                    data-testid="login-email-input"
                                    className="bg-zinc-900/50 border-white/10 text-zinc-100 placeholder:text-zinc-600"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-zinc-300">Password</Label>
                                <Input
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    type="password"
                                    placeholder="password123"
                                    data-testid="login-password-input"
                                    className="bg-zinc-900/50 border-white/10 text-zinc-100 placeholder:text-zinc-600"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-green-600 hover:bg-green-700 text-white shadow-[0_0_10px_rgba(34,197,94,0.2)]"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <LogIn className="mr-2" size={16} />}
                                Sign In
                            </Button>
                        </form>
                        <div className="mt-6 p-4 rounded-lg bg-zinc-800/50 border border-white/5 space-y-1.5" data-testid="demo-accounts">
                            <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-2">Demo Accounts</p>
                            <p className="text-xs text-zinc-500"><span className="text-zinc-400 font-mono">admin@ge.com</span> / password123 <span className="text-green-500/70">(Admin)</span></p>
                            <p className="text-xs text-zinc-500"><span className="text-zinc-400 font-mono">teamlead@ge.com</span> / password123 <span className="text-amber-500/70">(Team Lead)</span></p>
                            <p className="text-xs text-zinc-500"><span className="text-zinc-400 font-mono">management@co.com</span> / password123 <span className="text-blue-500/70">(Management)</span></p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
